import React, { useMemo } from "react";
import { Leaf, Sprout, Droplets, Sparkles, Flower2, FlaskConical } from "lucide-react";

/**
 * On-brand visual placeholder for products without a packaging photo.
 * Renders a warm, textured card with a category-appropriate icon + product name
 * in serif type. Variants are deterministic per product id so the same product
 * always gets the same visual.
 *
 * Props:
 *   name           - product name (required)
 *   category       - product category (optional, drives icon + palette)
 *   tags           - array of tags (optional, fallback for icon hinting)
 *   productId      - stable id for deterministic variant choice
 *   subtitle       - optional small text under name
 *   className      - additional classes for the outer container
 */

// Five palette variants — each pulls from the Badi brand colours
const PALETTES = [
  { bg: "#F1ECE4", glow: "#D49A44", ink: "#2D322B", accent: "#C05C42" }, // bone + amber
  { bg: "#E8DFD0", glow: "#6A8C5D", ink: "#2D322B", accent: "#5C6359" }, // sand + forest
  { bg: "#EFE6D6", glow: "#C05C42", ink: "#2D322B", accent: "#8F3A3A" }, // cream + terracotta
  { bg: "#E5DECF", glow: "#A88560", ink: "#2D322B", accent: "#6A4F3A" }, // taupe + chestnut
  { bg: "#EEE7DA", glow: "#9A8F7E", ink: "#2D322B", accent: "#5C6359" }, // beige + stone
];

// Lower-case tag/category keywords → which icon to use
const ICON_FOR_KEYWORD = {
  tea: Sprout,           botanical: Sprout,        leaf: Sprout,
  skin: Droplets,        skincare: Droplets,       serum: Droplets,
  soap: Droplets,        face: Droplets,           cream: Droplets,
  ritual: Sparkles,      mask: Sparkles,           glow: Sparkles,
  flower: Flower2,
  supplement: FlaskConical, foundational: FlaskConical, capsule: FlaskConical, prescription: FlaskConical,
};

function pickIcon(category, tags, name) {
  const haystack = [category, ...(tags || []), name || ""].join(" ").toLowerCase();
  for (const [keyword, Icon] of Object.entries(ICON_FOR_KEYWORD)) {
    if (haystack.includes(keyword)) return Icon;
  }
  return Leaf;
}

// Stable hash so each productId always picks the same palette
function hashCode(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export default function ProductPlaceholder({ name, category, tags, productId, subtitle, className = "" }) {
  const palette = useMemo(() => PALETTES[hashCode(productId || name) % PALETTES.length], [productId, name]);
  const Icon = useMemo(() => pickIcon(category, tags, name), [category, tags, name]);

  return (
    <div
      className={`relative w-full h-full flex flex-col items-center justify-center overflow-hidden ${className}`}
      style={{
        backgroundColor: palette.bg,
        // Subtle radial glow from the accent colour + grain via SVG noise overlay
        backgroundImage: `radial-gradient(circle at 30% 25%, ${palette.glow}33 0%, transparent 55%), radial-gradient(circle at 75% 80%, ${palette.accent}1A 0%, transparent 50%)`,
      }}
      data-testid="product-placeholder"
    >
      {/* Grain texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
        }}
      />
      {/* Soft circular ring */}
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: "62%",
          paddingBottom: "62%",
          border: `1px solid ${palette.accent}26`,
          top: "12%",
          left: "19%",
        }}
      />
      <div className="relative z-10 flex flex-col items-center text-center px-8 pt-6 pb-8">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
          style={{ backgroundColor: `${palette.accent}1A`, color: palette.accent }}
        >
          <Icon className="w-7 h-7" strokeWidth={1.2} />
        </div>
        <p
          className="font-serif leading-tight text-lg sm:text-xl"
          style={{ color: palette.ink, maxWidth: "16ch" }}
        >
          {name}
        </p>
        {subtitle && (
          <p className="text-[10px] uppercase tracking-[2.5px] mt-3" style={{ color: palette.accent }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
